
import { useEffect } from 'react';
import { userEmailConfigApi, UserEmailConfig, UserDetails } from '@/services/api/userEmailConfig';

interface UseEmailBootstrapProps {
  user: any;
  setUserDetails: (details: UserDetails | null) => void;
  setUserEmailConfig: (config: UserEmailConfig | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: boolean) => void;
}

export const useEmailBootstrap = ({
  user,
  setUserDetails,
  setUserEmailConfig,
  setLoading,
  setError,
}: UseEmailBootstrapProps) => {
  
  useEffect(() => {
    const bootstrap = async () => {
      if (!user?.id) {
        console.log('📧 Bootstrap skipped: No user ID');
        return;
      }
      
      try {
        console.log('📧 Bootstrapping email module for user:', user.id);
        setLoading(true);
        
        // Step 1: Get user details
        const userDetails = await userEmailConfigApi.getUserDetails(user.id);
        console.log('✅ User details loaded:', userDetails);
        setUserDetails(userDetails);
        
        // Step 2: Get user email config (IMAP/SMTP)
        const emailConfig = await userEmailConfigApi.getUserEmailConfig(user.id);
        console.log('✅ Email config loaded');
        setUserEmailConfig(emailConfig);
        
        console.log('✅ Email module bootstrapped successfully');
      } catch (error: any) {
        console.error('❌ Failed to bootstrap email module:', error);
        
        // Check if it's an authentication error (401)
        const isAuthError = error?.status === 401 || 
                           error?.message?.includes('401') ||
                           error?.message?.includes('Invalid token') ||
                           error?.message?.includes('Unauthorized');
        
        // Check if it's a 404 Not Found error (user profile/config doesn't exist yet)
        const isNotFoundError = error?.status === 404 || 
                               error?.response?.status === 404 ||
                               error?.message?.includes('404') ||
                               error?.message?.includes('not found') ||
                               error?.detail?.includes('not found');
        
        if (isAuthError) {
          console.error('🔒 Authentication error - email endpoints rejecting token');
          // Don't set error state for auth issues - let auth context handle it
        } else if (isNotFoundError) {
          console.log('📧 User email profile/config not found (404) - this is normal for new users');
          // Don't set error state for 404 - just continue with null user details/config
        } else {
          // Only set error for genuine server errors (5xx) or network failures
          setError(true);
        }
      } finally {
        setLoading(false);
      }
    };
    
    bootstrap();
  }, [user?.id, setUserDetails, setUserEmailConfig, setLoading, setError]);
};
